/**
 * Single place for AINA host `tenant` prop (web, RN, load-user-session).
 * Aligns with Android `userDto.tenantId` + enrolled program / profile fallbacks
 * and `mapModuleDataToDoubtSession` (program tenant, then profile; numeric DC fallback when key is non-numeric).
 */

export type AinaTenantResolutionInput = {
  /** Android `SecureSharedPreference` / `userDto.tenantId` */
  userTenantId?: string | number | null;
  /** Default enrolled program `tenant_id` (preferred for doubts APIs). */
  programTenantId?: string | number | null;
  /** Default profile `tenant_id`. */
  profileTenantId?: string | number | null;
  /** From `mapModuleDataToDoubtSession().tenantId` (program ?? profile). */
  doubtTenantKey?: string | null;
  /** From `mapModuleDataToDoubtSession().dcNumericTenantId` (numeric id or vertical when UUID). */
  doubtNumericTenantId?: string | null;
};

function nonEmptyString(v: unknown): string | null {
  if (v == null) return null;
  const s = String(v).trim();
  return s ? s : null;
}

/**
 * Resolves the tenant string passed into AINA RN / web / session payloads.
 * Order: user → program → profile → numeric preference when tenant key is non-numeric → tenant key → `"1"`.
 */
export function resolveAinaTenantForBundle(
  input: AinaTenantResolutionInput,
): string {
  const userT = nonEmptyString(input.userTenantId);
  if (userT) return userT;
  const progT = nonEmptyString(input.programTenantId);
  if (progT) return progT;
  const profT = nonEmptyString(input.profileTenantId);
  if (profT) return profT;

  const key = nonEmptyString(input.doubtTenantKey);
  if (key && /^\d+$/.test(key)) return key;

  const dc = nonEmptyString(input.doubtNumericTenantId);
  if (dc) return dc;

  if (key) return key;

  return "1";
}
